/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Spin, Tooltip, notification } from 'antd';
import { SendOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { Sparkles } from 'lucide-react';
import get from 'lodash/get';
import {
  SPACER_LOG_AI_FEATURES,
  deleteAiInsightChat,
  fetchAiInsightFeatures,
  generateAiInsight,
  getAiInsightChat,
  listAiInsightChats,
  mapChatThreadToMessages,
  cacheUsageForFeedbackTarget,
} from '../../services/aiInsightsApi';
import SpacerLogMarkdown from './SpacerLogMarkdown';
import SpacerLogAiCitations from './SpacerLogAiCitations';
import SpacerLogAiInsightPanel from './SpacerLogAiInsightPanel';
import SpacerLogAiAnswerActions from './SpacerLogAiAnswerActions';
import SpacerLogAiQuotaBar from './SpacerLogAiQuotaBar';
import SpacerLogAiChatSidebar from './SpacerLogAiChatSidebar';

const ASK_MODE = {
  OPS: 'ops',
  DOCS: 'docs',
};

const VIEW = {
  ASK: 'ask',
  INSIGHTS: 'insights',
};

const normalizeShift = (shift) => {
  if (shift === '0' || shift === 0) return 0;
  if (shift === '1' || shift === 1) return 1;
  return 'both';
};

const userInitials = () => {
  try {
    const raw = localStorage.getItem('user') || localStorage.getItem('userInfo');
    if (raw) {
      const u = JSON.parse(raw);
      const name = u.firstName || u.given_name || u.name || u.email || '';
      const parts = String(name).trim().split(/\s+/);
      if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      if (parts[0]) return parts[0].slice(0, 2).toUpperCase();
    }
  } catch {
    /* ignore */
  }
  return 'U';
};

/**
 * Ask Evans FAB + fullscreen modal (chatbot + history) and Insights briefs.
 * Ask Evans = chat only. Insights opens separately via dashboard CTAs —
 * same fullscreen shell, different content.
 */
const SpacerLogAiAskBar = ({
  context = {},
  activeView = 'daily',
  open: controlledOpen,
  onOpenChange,
  view: controlledView,
  onViewChange,
  initialFeature,
  tabScope = 'daily',
}) => {
  const [meta, setMeta] = useState(null);
  const [quota, setQuota] = useState(null);
  const [internalOpen, setInternalOpen] = useState(false);
  const [internalView, setInternalView] = useState(VIEW.ASK);
  const [mode, setMode] = useState(ASK_MODE.OPS);
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]);
  const [chatSessionId, setChatSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [threadLoading, setThreadLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const abortRef = useRef(null);
  const timerRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const chatSessionIdRef = useRef(null);
  const initials = useRef(userInitials()).current;

  const open = controlledOpen != null ? controlledOpen : internalOpen;
  const setOpen = (next) => {
    if (onOpenChange) onOpenChange(next);
    else setInternalOpen(next);
  };

  const view = controlledView != null ? controlledView : internalView;
  const setView = (next) => {
    if (onViewChange) onViewChange(next);
    else setInternalView(next);
  };

  const isInsights = view === VIEW.INSIGHTS;

  useEffect(() => {
    chatSessionIdRef.current = chatSessionId;
  }, [chatSessionId]);

  useEffect(() => {
    let cancelled = false;
    fetchAiInsightFeatures()
      .then((data) => {
        if (cancelled) return;
        setMeta(data);
        if (data.quota) setQuota(data.quota);
      })
      .catch(() => {
        if (!cancelled) setMeta({ enabled: false, ragConfigured: false, features: [] });
      });
    return () => {
      cancelled = true;
      if (abortRef.current) abortRef.current.abort();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const refreshSessions = useCallback(async () => {
    if (!context.projectId) {
      setSessions([]);
      return;
    }
    setSessionsLoading(true);
    try {
      const data = await listAiInsightChats({ projectId: context.projectId, limit: 40 });
      setSessions(data.sessions || []);
    } catch {
      /* list is best-effort — don't block Ask */
    } finally {
      setSessionsLoading(false);
    }
  }, [context.projectId]);

  useEffect(() => {
    if (open && view === VIEW.ASK) {
      refreshSessions();
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [open, view, refreshSessions]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, generating, threadLoading]);

  const aiEnabled = meta ? meta.enabled !== false : true;
  const ragConfigured = Boolean(meta?.ragConfigured);
  const hasProject = context.projectId != null && context.projectId !== '';

  const buildBody = useCallback(
    (feature, q, sessionId) => {
      const body = {
        feature,
        mode: 'ai',
        question: q,
      };
      if (sessionId) body.chatSessionId = sessionId;
      if (feature === SPACER_LOG_AI_FEATURES.RAG_QA) {
        if (context.projectId != null) body.projectId = context.projectId;
        return body;
      }
      body.projectId = context.projectId;
      if (context.sowId != null && context.sowId !== 'all') {
        body.sowId = Number(context.sowId);
      }
      if (context.logDate) body.logDate = context.logDate;
      if (context.shift != null) body.shift = normalizeShift(context.shift);
      if (activeView === 'project') {
        if (context.anchorDate) body.anchorDate = context.anchorDate;
        if (context.periodType) body.periodType = context.periodType;
        if (context.sowId != null) body.sowId = context.sowId;
      }
      return body;
    },
    [context, activeView]
  );

  const handleCancel = () => {
    if (abortRef.current) abortRef.current.abort();
  };

  const handleClose = () => {
    handleCancel();
    setOpen(false);
  };

  const handleNewChat = () => {
    if (abortRef.current) abortRef.current.abort();
    setMessages([]);
    setQuestion('');
    setChatSessionId(null);
    chatSessionIdRef.current = null;
    setGenerating(false);
    setElapsedSec(0);
    setThreadLoading(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleSelectChat = async (id) => {
    if (!id || generating) return;
    setThreadLoading(true);
    try {
      const thread = await getAiInsightChat(id);
      setChatSessionId(thread.session.id);
      chatSessionIdRef.current = thread.session.id;
      const mapped = mapChatThreadToMessages(thread.messages);
      // Persist any usage snapshots returned by history so later opens still show them
      mapped.forEach((m) => {
        if (m.role === 'assistant' && m.feedbackTargetId && m.usage) {
          cacheUsageForFeedbackTarget(m.feedbackTargetId, m.usage);
        }
      });
      setMessages(mapped);
      if (thread.session.feature === SPACER_LOG_AI_FEATURES.RAG_QA) {
        setMode(ASK_MODE.DOCS);
      } else if (thread.session.feature === SPACER_LOG_AI_FEATURES.NL_QA) {
        setMode(ASK_MODE.OPS);
      }
      setQuestion('');
    } catch (err) {
      notification.error({
        message: 'Could not open chat',
        description: String(get(err, 'error', get(err, 'message', err))),
      });
    } finally {
      setThreadLoading(false);
    }
  };

  const handleDeleteChat = (id) => {
    if (!id) return;
    Modal.confirm({
      title: 'Delete this chat?',
      content: 'The full thread will be removed. This cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      zIndex: 1300,
      onOk: async () => {
        try {
          await deleteAiInsightChat(id);
          if (String(chatSessionId) === String(id)) {
            handleNewChat();
          }
          await refreshSessions();
          notification.success({ message: 'Chat deleted' });
        } catch (err) {
          notification.error({
            message: 'Delete failed',
            description: String(get(err, 'error', get(err, 'message', err))),
          });
        }
      },
    });
  };

  const handleSend = async () => {
    const q = question.trim();
    if (!q || generating) return;

    const feature =
      mode === ASK_MODE.DOCS ? SPACER_LOG_AI_FEATURES.RAG_QA : SPACER_LOG_AI_FEATURES.NL_QA;

    if (feature === SPACER_LOG_AI_FEATURES.RAG_QA && !ragConfigured) {
      notification.warning({
        message: 'Documents unavailable',
        description: 'Connect-RAG is not configured on this environment yet.',
      });
      return;
    }
    if (feature === SPACER_LOG_AI_FEATURES.NL_QA && !hasProject) {
      notification.warning({ message: 'Select a project first' });
      return;
    }
    if (!aiEnabled) {
      notification.warning({ message: 'Ask Evans is temporarily unavailable.' });
      return;
    }

    const userMsg = { id: `u-${Date.now()}`, role: 'user', text: q };
    setMessages((prev) => [...prev, userMsg]);
    setQuestion('');

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setGenerating(true);
    setElapsedSec(0);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setElapsedSec((s) => s + 1), 1000);

    const sessionForRequest = chatSessionIdRef.current;

    try {
      const data = await generateAiInsight(buildBody(feature, q, sessionForRequest), {
        signal: controller.signal,
      });
      if (data.quota) setQuota(data.quota);
      if (data.chatSessionId) {
        setChatSessionId(data.chatSessionId);
        chatSessionIdRef.current = data.chatSessionId;
      }
      if (data.feedbackTargetId && data.usage) {
        cacheUsageForFeedbackTarget(data.feedbackTargetId, data.usage);
      }
      setMessages((prev) => [
        ...prev,
        {
          id: `a-${Date.now()}`,
          role: 'assistant',
          markdown: data.markdown || 'No response.',
          citations: data.citations,
          fallback: data.fallback,
          mode: data.mode,
          error: data.error,
          feature,
          question: q,
          usage: data.usage,
          feedbackTargetId: data.feedbackTargetId,
          feedbackRating: null,
          ragSessionId: data.ragSessionId || null,
        },
      ]);
      refreshSessions();
    } catch (err) {
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || err?.name === 'AbortError') {
        return;
      }
      const msg = get(err, 'error', get(err, 'message', 'Failed to get an answer.'));
      setMessages((prev) => [
        ...prev,
        {
          id: `e-${Date.now()}`,
          role: 'assistant',
          markdown: msg,
          isError: true,
        },
      ]);
    } finally {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setGenerating(false);
      abortRef.current = null;
    }
  };

  const placeholder =
    mode === ASK_MODE.DOCS
      ? 'Ask a question about Spacer Log documents…'
      : activeView === 'daily'
        ? 'Ask a question about this day…'
        : 'Ask a question about this period…';

  return (
    <>
      {!open ? (
        <button
          type="button"
          className="spacer-ask-evans-fab"
          onClick={() => {
            setView(VIEW.ASK);
            setOpen(true);
          }}
          aria-label="Ask Evans"
        >
          <Sparkles size={16} strokeWidth={2.25} aria-hidden />
          <span>Ask Evans</span>
        </button>
      ) : null}

      <Modal
        visible={open}
        onCancel={handleClose}
        footer={null}
        width="100%"
        centered={false}
        destroyOnClose={false}
        className="spacer-ask-evans-modal"
        wrapClassName="spacer-ask-evans-modal-wrap"
        closable={false}
        maskClosable
        zIndex={1200}
        style={{ top: 0, paddingBottom: 0, maxWidth: '100vw', margin: 0 }}
        bodyStyle={{ padding: 0, height: '100%' }}
      >
        <div className="spacer-ask-evans spacer-ask-evans--fullscreen">
          <header className="spacer-ask-evans__header">
            <div className="spacer-ask-evans__header-left">
              <Tooltip title="Back">
                <button
                  type="button"
                  className="spacer-ask-evans__back"
                  onClick={handleClose}
                  aria-label="Back"
                >
                  <ArrowLeftOutlined />
                </button>
              </Tooltip>
              <div className="spacer-ask-evans__titles">
                <h2 className="spacer-ask-evans__title">
                  Ask Evans · {isInsights ? 'Insights' : 'Chat'}
                </h2>
                <p className="spacer-ask-evans__subtitle">
                  {isInsights
                    ? 'On-demand period / day briefs from current Spacer Log filters'
                    : 'Chat with Spacer Log ops data and documents'}
                </p>
              </div>
            </div>
          </header>

          {isInsights ? (
            <>
              <SpacerLogAiInsightPanel
                open={open && isInsights}
                initialFeature={initialFeature}
                tabScope={tabScope}
                context={context}
                onQuota={setQuota}
              />
              <div className="spacer-ask-evans__insights-foot">
                <SpacerLogAiQuotaBar quota={quota} />
              </div>
            </>
          ) : (
            <div className="spacer-ask-evans__workspace">
              <SpacerLogAiChatSidebar
                sessions={sessions}
                activeId={chatSessionId}
                loading={sessionsLoading}
                onNewChat={handleNewChat}
                onSelect={handleSelectChat}
                onDelete={handleDeleteChat}
              />

              <div className="spacer-ask-evans__main">
                <div className="spacer-ask-evans__toolbar">
                  <div className="spacer-ask-evans__modes" role="tablist">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={mode === ASK_MODE.OPS}
                      className={`spacer-ask-evans__mode${
                        mode === ASK_MODE.OPS ? ' spacer-ask-evans__mode--active' : ''
                      }`}
                      onClick={() => setMode(ASK_MODE.OPS)}
                    >
                      Ops data
                    </button>
                    <Tooltip
                      title={
                        ragConfigured
                          ? 'Answers from ingested Spacer Log documents'
                          : 'Documents need Connect-RAG (ragConfigured)'
                      }
                    >
                      <button
                        type="button"
                        role="tab"
                        aria-selected={mode === ASK_MODE.DOCS}
                        className={`spacer-ask-evans__mode${
                          mode === ASK_MODE.DOCS ? ' spacer-ask-evans__mode--active' : ''
                        }`}
                        disabled={!ragConfigured}
                        onClick={() => setMode(ASK_MODE.DOCS)}
                      >
                        Documents
                      </button>
                    </Tooltip>
                  </div>
                  <button type="button" className="spacer-ask-evans__new" onClick={handleNewChat}>
                    + New chat
                  </button>
                </div>

                <div className="spacer-ask-evans__messages" ref={scrollRef}>
                  {threadLoading ? (
                    <div className="spacer-ask-evans__empty">
                      <Spin />
                      <p className="spacer-ask-evans__empty-text">Loading chat…</p>
                    </div>
                  ) : null}

                  {!threadLoading && !messages.length && !generating ? (
                    <div className="spacer-ask-evans__empty">
                      <div className="spacer-ask-evans__empty-avatar" aria-hidden>
                        AE
                      </div>
                      <div className="spacer-ask-evans__empty-title">Ask Evans</div>
                      <p className="spacer-ask-evans__empty-text">
                        Your Spacer Log chatbot — ask about today&apos;s ops metrics or ingested
                        reports (e.g. Golfinho). Threads are saved so you can reopen them anytime.
                      </p>
                      <p className="spacer-ask-evans__empty-hint">
                        Tip: be specific — “Summarize Golfinho day shift 2026-09-08” works better
                        than “summary so far”.
                      </p>
                    </div>
                  ) : null}

                  {!threadLoading
                    ? messages.map((m) =>
                        m.role === 'user' ? (
                          <div
                            key={m.id}
                            className="spacer-ask-evans__row spacer-ask-evans__row--user"
                          >
                            <div className="spacer-ask-evans__bubble spacer-ask-evans__bubble--user">
                              {m.text}
                            </div>
                            <div className="spacer-ask-evans__avatar spacer-ask-evans__avatar--user">
                              {initials}
                            </div>
                          </div>
                        ) : (
                          <div
                            key={m.id}
                            className="spacer-ask-evans__row spacer-ask-evans__row--bot"
                          >
                            <div className="spacer-ask-evans__avatar spacer-ask-evans__avatar--ae">
                              AE
                            </div>
                            <div
                              className={`spacer-ask-evans__bubble spacer-ask-evans__bubble--bot${
                                m.isError ? ' spacer-ask-evans__bubble--error' : ''
                              }`}
                            >
                              {(m.fallback || m.mode === 'template') && !m.isError ? (
                                <div className="spacer-ask-evans__fallback">
                                  {m.fallback
                                    ? 'AI unavailable — showing template answer'
                                    : 'Deterministic (template) answer'}
                                  {m.error ? ` · ${m.error}` : ''}
                                </div>
                              ) : null}
                              <SpacerLogMarkdown content={m.markdown} />
                              <SpacerLogAiCitations citations={m.citations} />
                              {!m.isError ? (
                                <SpacerLogAiAnswerActions
                                  key={`actions-${m.id}`}
                                  markdown={m.markdown}
                                  usage={m.usage}
                                  feedbackTargetId={m.feedbackTargetId}
                                  initialRating={m.feedbackRating}
                                  feature={m.feature}
                                  question={m.question}
                                  projectId={context.projectId}
                                  ragSessionId={m.ragSessionId}
                                  title="Ask Evans Brief"
                                  eyebrow="SPACER LOG - ASK EVANS REPORT"
                                  subtitle="Prepared for operations - CONFIDENTIAL"
                                />
                              ) : null}
                            </div>
                          </div>
                        )
                      )
                    : null}

                  {generating ? (
                    <div className="spacer-ask-evans__row spacer-ask-evans__row--bot">
                      <div className="spacer-ask-evans__avatar spacer-ask-evans__avatar--ae">AE</div>
                      <div className="spacer-ask-evans__bubble spacer-ask-evans__bubble--bot spacer-ask-evans__thinking">
                        <Spin size="small" />
                        <span>Thinking… {elapsedSec}s</span>
                        <button
                          type="button"
                          className="spacer-ask-evans__link-btn"
                          onClick={handleCancel}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>

                <footer className="spacer-ask-evans__footer">
                  {!aiEnabled ? (
                    <div className="spacer-ask-evans__fallback">
                      Ask Evans is temporarily unavailable
                    </div>
                  ) : null}
                  <form
                    className="spacer-ask-evans__composer"
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleSend();
                    }}
                  >
                    <input
                      ref={inputRef}
                      type="text"
                      className="spacer-ask-evans__input"
                      value={question}
                      onChange={(e) => setQuestion(e.target.value)}
                      placeholder={placeholder}
                      disabled={generating || !aiEnabled || threadLoading}
                      maxLength={2000}
                      autoComplete="off"
                    />
                    <button
                      type="submit"
                      className="spacer-ask-evans__send"
                      disabled={
                        generating || !question.trim() || !aiEnabled || threadLoading
                      }
                    >
                      <SendOutlined />
                      <span>Send</span>
                    </button>
                  </form>
                  <SpacerLogAiQuotaBar quota={quota} />
                  <p className="spacer-ask-evans__disclaimer">
                    Answers use your current Spacer Log filters. Documents mode only searches
                    ingested reports. History is saved per project.
                  </p>
                </footer>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default SpacerLogAiAskBar;
